// Controller Setup wizard — walks the player through binding their gamepad,
// whatever its button/axis layout. While open it sets the global "capturing"
// flag so heroController + gamepadCursor stop reacting (presses bind instead of
// firing actions). Auto-opens once on first controller connect if unconfigured;
// also re-openable from Settings via Events.OpenControllerSetup.

import { Events, on, emit } from './eventBus.js'
import {
  SETUP_STEPS, DEFAULTS, getBindings, saveBindings, resetBindings,
  isConfigured, setCapturing,
} from '../game/gamepadBindings.js'

let root = null
let work = null
let stepIdx = 0
let settled = false
let raf = 0
let open = false

export function mountControllerSetup() {
  if (root || typeof document === 'undefined') return
  root = document.createElement('div')
  root.className = 'gp-setup'
  root.innerHTML = `
    <div class="gp-setup__backdrop" data-gp-cancel></div>
    <div class="gp-setup__card" role="dialog" aria-modal="true" aria-label="Controller setup">
      <button class="gp-setup__x" data-gp-cancel aria-label="Close">×</button>
      <div class="gp-setup__title">🎮 Controller Setup</div>
      <div class="gp-setup__progress" data-gp-progress></div>
      <div class="gp-setup__prompt" data-gp-prompt>—</div>
      <div class="gp-setup__hint" data-gp-hint></div>
      <div class="gp-setup__row">
        <button class="gp-setup__btn ghost" data-gp-skip>Skip this</button>
        <button class="gp-setup__btn ghost" data-gp-finish>Finish (defaults for rest)</button>
      </div>
      <div class="gp-setup__row">
        <button class="gp-setup__btn ghost" data-gp-reset>Reset to defaults</button>
        <button class="gp-setup__btn ghost" data-gp-cancel2 data-gp-cancel>Cancel</button>
      </div>
    </div>
  `
  document.body.appendChild(root)

  root.querySelectorAll('[data-gp-cancel]').forEach((b) => b.addEventListener('click', cancel))
  root.querySelector('[data-gp-skip]').addEventListener('click', () => advance())
  root.querySelector('[data-gp-finish]').addEventListener('click', finish)
  root.querySelector('[data-gp-reset]').addEventListener('click', () => { resetBindings(); cancel() })
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) cancel() })

  on(Events.OpenControllerSetup, openWizard)

  // First-connect helper: if a controller appears and we've never been set up,
  // pop the wizard so the player can map it immediately.
  window.addEventListener('gamepadconnected', () => {
    if (!isConfigured() && !open) openWizard()
  })
}

function openWizard() {
  if (open) return
  open = true
  work = { ...DEFAULTS, ...getBindings() }
  stepIdx = 0
  settled = false
  setCapturing(true)
  root.classList.add('on')
  render()
  cancelAnimationFrame(raf)
  raf = requestAnimationFrame(loop)
}

function close() {
  open = false
  setCapturing(false)
  cancelAnimationFrame(raf)
  root.classList.remove('on')
}
function cancel() { if (open) close() }            // abort without saving
function finish() { if (!open) return; saveBindings(work); close() } // keep what's bound so far

function render() {
  const step = SETUP_STEPS[stepIdx]
  root.querySelector('[data-gp-progress]').textContent = `Step ${stepIdx + 1} of ${SETUP_STEPS.length}`
  root.querySelector('[data-gp-prompt]').textContent = step ? step.label : ''
  root.querySelector('[data-gp-hint]').textContent =
    'No controller detected — connect one and press any button.'
}

function advance() {
  settled = false
  stepIdx += 1
  if (stepIdx >= SETUP_STEPS.length) finish()
  else render()
}

function firstPad() {
  const pads = navigator.getGamepads ? navigator.getGamepads() : []
  for (const p of pads) if (p && p.connected) return p
  return null
}

function loop() {
  if (!open) return
  raf = requestAnimationFrame(loop)
  const gp = firstPad()
  const hintEl = root.querySelector('[data-gp-hint]')
  if (!gp) { hintEl.textContent = 'No controller detected — connect one and press any button.'; return }
  hintEl.textContent = gp.id?.slice(0, 48) || ''

  const step = SETUP_STEPS[stepIdx]
  if (!step) return
  const anyBtn = gp.buttons.some((b) => b.pressed)
  const maxAxis = Math.max(0, ...gp.axes.map((a) => Math.abs(a)))

  // Settle gate: wait for the pad to return to neutral before capturing, so a
  // held button from the previous step doesn't bleed into this one.
  if (!settled) { if (!anyBtn && maxAxis < 0.2) settled = true; return }

  if (step.type === 'button') {
    const i = gp.buttons.findIndex((b) => b.pressed)
    if (i >= 0) { work[step.key] = i; advance() }
    return
  }

  // axisRight / axisDown — capture the most-deflected axis + its sign so we
  // normalize that direction to positive.
  let bi = -1
  let bv = 0
  gp.axes.forEach((v, i) => { if (Math.abs(v) > Math.abs(bv)) { bv = v; bi = i } })
  if (Math.abs(bv) > 0.6 && bi >= 0) {
    const sign = bv >= 0 ? 1 : -1
    if (step.type === 'axisRight') {
      if (step.group === 'move') { work.moveX = bi; work.moveXInv = sign }
      else { work.pointerX = bi; work.pointerXInv = sign }
    } else {
      if (step.group === 'move') { work.moveY = bi; work.moveYInv = sign }
      else { work.pointerY = bi; work.pointerYInv = sign }
    }
    advance()
  }
}

// Convenience for callers that prefer a direct open over the event.
export function openControllerSetup() { emit(Events.OpenControllerSetup) }
